import { useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useIsMounted, useOverlayContext } from 'seagull';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { useIoContext, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { MAX_FILE_SIZE_BYTES } from '../ImportAgreementModal.utils';
export const useImportAgreement = (testIds, onSuccess)=>{
    const [modalState, setModalState] = useState('upload');
    const [isUploading, setIsUploading] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [errorReason, setErrorReason] = useState(null);
    const [conversationId, setConversationId] = useState(null);
    const [extractionSuccess, setExtractionSuccess] = useState(false);
    const extractionRef = useRef(null);
    const successConversationIdRef = useRef(null);
    const { api } = useStoreActions();
    const { uploadFileToCloudStorage } = useIoContext();
    const { showErrorToast } = useOverlayContext();
    const { t } = useLocaleKeys();
    const isMounted = useIsMounted();
    const processFile = async (file)=>{
        if (file.size > MAX_FILE_SIZE_BYTES) {
            showErrorToast(t.agreementImport.uploadModal.fileSizeError(), testIds.FileSizeErrorToast());
            return;
        }
        setIsUploading(true);
        setFileName(file.name);
        try {
            const id = generateUniqueId('attachment');
            const filesData = [
                {
                    id,
                    displayName: file.name,
                    size: file.size
                }
            ];
            const { urls } = await api.business.getUploadUrls(filesData);
            const uploadInfo = urls[0];
            await uploadFileToCloudStorage(uploadInfo.url, file, uploadInfo.headers);
            setIsUploading(false);
            setModalState('analyzing');
            try {
                const response = await api.agreementImport.extract({
                    fileId: id
                });
                if (!isMounted()) return;
                if (response.success) {
                    if (response.conversationId == null) {
                        setErrorReason('extraction_failed');
                        setModalState('error');
                        return;
                    }
                    extractionRef.current = response.extraction;
                    successConversationIdRef.current = response.conversationId;
                    setConversationId(response.conversationId);
                    setExtractionSuccess(true);
                } else {
                    setErrorReason(response.reason);
                    if (response.conversationId) {
                        setConversationId(response.conversationId);
                    }
                    setModalState('error');
                }
            } catch (unused) {
                if (!isMounted()) return;
                setErrorReason('extraction_failed');
                setModalState('error');
            }
        } catch (error) {
            Sentry.captureException(error);
            showErrorToast(t.agreementImport.uploadModal.uploadError(), testIds.UploadErrorToast());
            setIsUploading(false);
        }
    };
    const submitFeedback = (rating)=>{
        if (conversationId == null) return;
        api.agreementImport.feedback({
            conversationId,
            rating
        }).catch(()=>{});
    };
    const resetToUpload = ()=>{
        setModalState('upload');
        setErrorReason(null);
        setConversationId(null);
        setExtractionSuccess(false);
        setFileName(null);
    };
    const notifySuccess = ()=>{
        if (!isMounted()) return;
        if (extractionRef.current == null || successConversationIdRef.current == null) return;
        onSuccess(extractionRef.current, successConversationIdRef.current);
    };
    return {
        modalState,
        isUploading,
        fileName,
        errorReason,
        conversationId,
        extractionSuccess,
        processFile,
        submitFeedback,
        resetToUpload,
        notifySuccess
    };
};
